import { useState } from "react";
import { Warning, X } from "@phosphor-icons/react";
import { api, setSessionToken } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function Account() {
  const { user, setUser } = useAuth();
  const [showDelete, setShowDelete] = useState(false);
  const [email, setEmail] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const canDelete = email.trim().toLowerCase() === user?.email?.toLowerCase() && confirmation === "ELIMINAR";

  const deleteAccount = async () => {
    if (!canDelete || busy) return;
    setBusy(true);
    setError("");
    try {
      await api.delete("/auth/account", { data: { email: email.trim(), confirmation } });
      setSessionToken("");
      setUser(null);
      window.location.assign("/eliminar-cuenta?deleted=1");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "No se pudo eliminar la cuenta. Inténtalo de nuevo.");
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto fade-up" data-testid="account-page">
      <p className="eyebrow mb-4">tu espacio</p>
      <h1 className="font-serif text-4xl sm:text-5xl">Cuenta</h1>

      <section className="mt-12 py-8 border-y border-[hsl(var(--border))] flex flex-col sm:flex-row sm:items-center gap-5">
        {user?.picture && <img src={user.picture} alt="Foto de perfil" className="w-16 h-16 rounded-full object-cover" data-testid="account-profile-image" />}
        <div className="min-w-0">
          <p className="font-serif text-2xl" data-testid="account-name">{user?.name}</p>
          <p className="text-sm text-[hsl(var(--muted-foreground))] break-all" data-testid="account-email">{user?.email}</p>
        </div>
      </section>

      <section className="mt-16 pt-8 border-t border-[hsl(var(--border))]" data-testid="account-danger-zone">
        <div className="flex items-start gap-3">
          <Warning size={24} className="text-[hsl(var(--destructive))] shrink-0 mt-1" />
          <div>
            <h2 className="font-serif text-3xl">Eliminar cuenta</h2>
            <p className="mt-3 text-sm sm:text-base text-[hsl(var(--muted-foreground))] leading-relaxed max-w-2xl">
              Se eliminarán definitivamente tu perfil, sesiones, recetas, categorías y conversaciones de soporte. Los registros de pagos que deban conservarse quedarán disociados de tu cuenta.
            </p>
            <button onClick={() => setShowDelete(true)} className="mt-6 px-5 py-3 rounded-full border border-[hsl(var(--destructive))] text-[hsl(var(--destructive))] text-sm font-semibold btn-press" data-testid="open-delete-account-dialog-button">
              Eliminar mi cuenta
            </button>
          </div>
        </div>
      </section>

      {showDelete && (
        <div className="fixed inset-0 z-50 bg-black/45 p-4 flex items-center justify-center" role="presentation" data-testid="delete-account-dialog-backdrop">
          <div className="w-full max-w-lg bg-white rounded-2xl p-6 sm:p-8 shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="delete-account-title" data-testid="delete-account-dialog">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="eyebrow mb-2">acción irreversible</p>
                <h2 id="delete-account-title" className="font-serif text-3xl">Confirma la eliminación</h2>
              </div>
              <button onClick={() => setShowDelete(false)} disabled={busy} aria-label="Cerrar" className="w-10 h-10 inline-flex items-center justify-center" data-testid="close-delete-account-dialog-button"><X size={20} /></button>
            </div>

            <div className="mt-6 grid gap-5">
              <label className="block">
                <span className="block text-xs font-semibold uppercase text-[hsl(var(--muted-foreground))] mb-2">Tu email</span>
                <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} disabled={busy} className="w-full border border-[hsl(var(--border))] rounded-xl px-4 py-3 focus:outline-none focus:border-[hsl(var(--primary))]" data-testid="delete-account-email-input" />
              </label>
              <label className="block">
                <span className="block text-xs font-semibold uppercase text-[hsl(var(--muted-foreground))] mb-2">Escribe ELIMINAR</span>
                <input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} disabled={busy} className="w-full border border-[hsl(var(--border))] rounded-xl px-4 py-3 focus:outline-none focus:border-[hsl(var(--primary))]" data-testid="delete-account-confirmation-input" />
              </label>
            </div>

            {error && <p className="mt-4 text-sm text-[hsl(var(--destructive))]" role="alert" data-testid="delete-account-error">{error}</p>}
            <button onClick={deleteAccount} disabled={!canDelete || busy} className="mt-6 w-full px-5 py-3 rounded-full bg-[hsl(var(--destructive))] text-white text-sm font-semibold disabled:opacity-40 btn-press" data-testid="confirm-delete-account-button">
              {busy ? "Eliminando…" : "Eliminar definitivamente"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}